import { format } from 'date-fns'

import type {
  AccountCoverage,
  CoverageStatus,
  MonthlyStatus,
} from '@/modules/projects/components/StatementCoverage/types'
import { cn } from '@/utils'
import {
  laneNumberOf,
  type MapLane,
  type MonthNumber,
  monthNumberOf,
} from './map'
import { monthsWorthShowing } from './memberCardsOf'
import type { DatePeriod } from './types'

// Both read the live coverage grid. A statement is read for one account's number, since one mailed
// for several can reconcile for one and not another. No number means its worst account.
export type StatusFor = (
  statementId: string,
  last4: string | null,
) => CoverageStatus
export type EmptyMonthFor = (
  last4: string | null,
  period: DatePeriod,
) => MonthlyStatus | null

// The coverage grid paints SVG, so its palette is `fill-*` classes. A chip and a swatch are
// divs, so they need the same shades as backgrounds (the same ones the coverage legend uses).
export const COVERAGE_SWATCH_CLASS: Record<CoverageStatus, string> = {
  reconciled: 'bg-emerald-500',
  needs_review: 'bg-amber-500',
  unverifiable: 'bg-slate-400',
  not_checked: 'bg-blue-500',
  member_activity: 'bg-violet-300',
  verified_gap: 'bg-emerald-100',
  missing: 'bg-red-500',
}

// The lower bars of a statement joined across several accounts. A lighter tint of the same shade
// reads as more of the bar above, not as a statement of its own with nothing in it.
export const COVERAGE_CONTINUATION_CLASS: Record<CoverageStatus, string> = {
  reconciled: 'bg-emerald-500/35',
  needs_review: 'bg-amber-500/35',
  unverifiable: 'bg-slate-400/35',
  not_checked: 'bg-blue-500/35',
  member_activity: 'bg-violet-300/50',
  verified_gap: 'bg-emerald-100/60',
  missing: 'bg-red-500/35',
}

const LIGHT_FILLS: CoverageStatus[] = ['member_activity', 'verified_gap']

export const coverageTextClass = (status: CoverageStatus) =>
  LIGHT_FILLS.includes(status) ? 'text-foreground' : 'text-white'

// A missing month is a statement the client still owes, the one gap that is work left, so it is
// the loudest thing on the map. The explained gaps stay pale behind a dashed edge.
export const emptyMonthClass = (status: CoverageStatus) =>
  status === 'missing'
    ? 'border-transparent bg-red-500 font-medium text-[11px] text-white hover:bg-red-600'
    : cn('border-dashed border-neutral-400/60', COVERAGE_SWATCH_CLASS[status])

// Worst first, so the tally reads as a to-do list.
export const TALLIED: CoverageStatus[] = [
  'missing',
  'needs_review',
  'unverifiable',
  'not_checked',
  'member_activity',
  'verified_gap',
  'reconciled',
]

// The live coverage by account number: the months each saved cycle owns, by segment, and the
// months worth showing, by 'yyyy-MM'.
export const coverageIndex = (accounts: AccountCoverage[]) => {
  const ownedMonths = new Map<string, Map<number, MonthNumber[]>>()
  const monthsByNumber = new Map<string, Map<string, MonthlyStatus>>()
  for (const account of accounts) {
    const bySegment = new Map<number, MonthNumber[]>()
    for (const entry of account.monthlyStatus) {
      if (entry.segmentId === undefined) continue
      bySegment.set(entry.segmentId, [
        ...(bySegment.get(entry.segmentId) ?? []),
        monthNumberOf(entry.date),
      ])
    }
    ownedMonths.set(account.id, bySegment)
    monthsByNumber.set(
      account.id,
      new Map(
        monthsWorthShowing(account).map((entry) => [
          format(entry.date, 'yyyy-MM'),
          entry,
        ]),
      ),
    )
  }
  return { ownedMonths, monthsByNumber }
}

// Every month a lane holds no statement in is read against the live coverage once, here. The
// tally, the filter and the map all read the answer off the cell.
export const withEmptyMonths = (
  lanes: MapLane[],
  emptyMonthFor: EmptyMonthFor,
): MapLane[] =>
  lanes.map((lane) => ({
    ...lane,
    years: lane.years.map((year) => ({
      ...year,
      cells: year.cells.map((cell) => ({
        ...cell,
        coverage:
          cell.views.length > 0
            ? null
            : emptyMonthFor(laneNumberOf(lane), cell.period),
      })),
    })),
  }))

const emptyMonthsOf = (lane: MapLane) =>
  lane.years.flatMap((year) => year.cells.map((cell) => cell.coverage))

export const missingMonths = (lane: MapLane) =>
  emptyMonthsOf(lane).filter((month) => month?.status === 'missing').length

const NOTHING_COUNTED: Record<CoverageStatus, number> = {
  reconciled: 0,
  needs_review: 0,
  unverifiable: 0,
  not_checked: 0,
  member_activity: 0,
  verified_gap: 0,
  missing: 0,
}

export const tallyCoverage = (
  lanes: MapLane[],
  statusFor: StatusFor,
): Record<CoverageStatus, number> => {
  const counts = { ...NOTHING_COUNTED }

  // A statement drawn on two rows, or in two accounts' lanes, is still one statement.
  const drawn = new Set(
    lanes.flatMap((lane) =>
      lane.years.flatMap((year) =>
        year.spans.map((span) => span.view.statement.id),
      ),
    ),
  )
  for (const statementId of drawn) counts[statusFor(statementId, null)] += 1

  for (const lane of lanes) {
    for (const month of emptyMonthsOf(lane)) {
      if (month) counts[month.status] += 1
    }
  }

  return counts
}

const laneHolds = (
  lane: MapLane,
  status: CoverageStatus,
  statusFor: StatusFor,
) =>
  lane.years.some((year) =>
    year.spans.some(
      (span) =>
        statusFor(span.view.statement.id, laneNumberOf(lane)) === status,
    ),
  ) || emptyMonthsOf(lane).some((month) => month?.status === status)

// Picking a status in the tally is a question: where is it? Only the accounts that answer stay.
export const lanesHolding = (
  lanes: MapLane[],
  status: CoverageStatus | null,
  statusFor: StatusFor,
) =>
  status === null
    ? lanes
    : lanes.filter((lane) => laneHolds(lane, status, statusFor))
